/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import { Form, Select, Input, Button } from 'semantic-ui-react';

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { useAuthContext } from '../../../contexts/AuthContext';
import { FlagBuilder, NoteBuilder } from '../../../fhir-sdoh';

import { useState } from 'react';
import type { DocumentReference, Flag } from 'fhir/r4';

import type { NoteThread, PractitionerOption } from '../../../types';

/**
 * Transcribed from the producers, `notes/list.tsx:313` and `notes/listAll.tsx:499`,
 * which render this with identical props inside their per-thread reply row.
 */
interface AddChildProps {
  patientId: string;
  /** The parent note's id, which keys `replyTextList` and `tableData`. */
  parentId: string;
  createNote: (type: string, notePayload: DocumentReference, parentId: string, flagpayload?: Flag) => void;
  type: string;
  /** The notes bucket (`GENERAL`/`ALL`/…), used to look up `noteCategories`. */
  notesType: string;
  code: string;
  /** Reply drafts keyed by parent note id. */
  replyTextList: Record<string, string>;
  setReplyTextList: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  noteCategories: Record<string, { code: string; display: string }>;
  org: string;
  practitionerData: PractitionerOption[];
  /** Passed by both producers but never read here. */
  childNote?: unknown;
  tableData: Record<string, NoteThread>;
}

function AddChild(props: AddChildProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;
  const practitionerId = authInfo.practitionerId;
  const [selectedPractitioners, setSelectedPractitioners] = useState<string[]>([]);
  const practitioners = props.practitionerData;
  // `string[] | undefined` because the JSX handler below reassigns this binding to
  // `undefined` on every dropdown change (see `handleNoteTagChange`).
  // `let`, and the rule is wrong here: the JSX handler below assigns to `noteTag`
  // directly (not through `setNoteTag`), so the binding really is reassigned.
  // eslint-disable-next-line prefer-const
  let [noteTag, setNoteTag] = useState<string[] | undefined>([]);

  const handleReplyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    props.setReplyTextList((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /*
   * `e.target` is the clicked option/delete-icon element; semantic-ui types it only as `EventTarget`.
   *
   * SUSPECTED DEFECT (pre-existing): the sole caller passes `(noteTag = undefined)`,
   * an assignment expression that overwrites the `noteTag` binding with `undefined`
   * on every dropdown change before this function runs. `_value` is unread, so the
   * argument itself is inert, but the assignment is not: the `noteTag?.length > 0`
   * test in `validateChild` and the `value={noteTag}` binding below both read that
   * same variable. Typed `undefined` to record what is actually passed.
   */
  const handleNoteTagChange = (e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }, _value: undefined) => {
    //to delete the practitioner if delete option is selected
    if (e.target?.className === 'delete icon') {
      setSelectedPractitioners((prevState) =>
        prevState.filter((practitioner) => practitioner !== e.target?.previousSibling?.textContent)
      );
      practitioners.forEach((note: PractitionerOption) => {
        if (note.text === e.target?.previousSibling?.textContent) {
          // Asserted, not guarded: `prevState` is genuinely `undefined` after the
          // dropdown handler's `noteTag = undefined` assignment (see the block above),
          // so this call throws. Inserting `?.` would convert that crash into a silent
          // no-op, which is a behaviour change hiding the defect rather than reporting it.
          setNoteTag((prevState) => (prevState as string[]).filter((practitionerId) => practitionerId !== note.value));
        }
      });
    } else if (e.target?.className === 'dropdown icon clear') {
      setNoteTag([]);
      setSelectedPractitioners([]);
    } else {
      practitioners.forEach((note) => {
        if (note.text === e.target.textContent) {
          setSelectedPractitioners((prevState) => [...prevState, e.target.textContent]);
          // Asserted rather than defaulted, for the same reason as the delete branch:
          // spreading `undefined` throws, and `?? []` would substitute a value the
          // original never produced.
          setNoteTag((prevState) => [...(prevState as string[]), note.value]);
        }
      });
    }
  };

  /* `tag` admits `undefined` because the JSX passes `noteTag`, which the dropdown handler nulls out. */
  const validateChild = (parentId: string, type: string, code: string, tag: string[] | undefined) => {
    const childNote = {
      noteType: type,
      noteText: props.replyTextList[parentId],
      noteTypeCode: code,
      noteTag: tag,
    };
    let taggedPractitioners = '';
    if (selectedPractitioners?.length > 0) {
      selectedPractitioners.forEach((practitioner) => {
        taggedPractitioners += ` #` + practitioner;
      });
    }
    const referralId = props?.tableData?.[parentId]?.referralId;

    if (props.replyTextList[parentId] && props.replyTextList[parentId] !== '') {
      const notePayload = NoteBuilder.buildComment(
        // `patientId` reaches the notes panes as `string | undefined` — see the
        // SUSPECTED DEFECT on `notes/tabs.tsx`. `.toString()` throws on absence here
        // rather than posting against `patient=undefined`.
        props.patientId.toString(),
        parentId,
        (selectedPractitioners?.length > 0 ? childNote.noteText + taggedPractitioners : childNote.noteText) as string,
        practitionerId,
        props.noteCategories[props.notesType]!.code,
        props.noteCategories[props.notesType]!.display,
        practitionerName,
        // `unknown[]` — the raw `fhirpath.evaluate` array the referral id comes off.
        referralId as string[] | undefined
      );
      if ((noteTag?.length ?? 0) > 0) {
        const flagpayload: Flag = FlagBuilder.build(
          props.patientId,
          authInfo.practitionerId,
          { code: 'admin', display: 'administrative' },
          { code: 'ALERT', display: 'Alert' },
          childNote.noteText + ' :Tagged you by ' + authInfo.practitionerName,
          authInfo.practitionerName,
          props.org,
          noteTag!.toString() // assignedTo
        );
        props.createNote(type, notePayload, parentId, flagpayload);
      } else {
        props.createNote(type, notePayload, parentId);
      }
    }
    setNoteTag([]);
    setSelectedPractitioners([]);
  };
  return (
    <Form reply>
      <Form.Group widths="equal">
        <Form.Field
          id="form-input-control-reply-comment"
          required
          control={Input}
          placeholder="Reply"
          width={5}
          name={props.parentId}
          value={props.replyTextList[props.parentId]}
          onChange={handleReplyChange}
          rows={4}
        />
        <Form.Field
          id="form-select-control-notes-tag"
          clearable
          multiple
          control={Select}
          width={3}
          options={practitioners}
          selectOnBlur={false}
          placeholder="Tag"
          name="noteTag"
          value={noteTag}
          onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }) => handleNoteTagChange(e, (noteTag = undefined))}
          search
          searchInput={{ id: 'form-select-control-notes-tag' }}
        />
        <Button
          id="form-button-reply-comment"
          onClick={() => validateChild(props.parentId, props.type, props.code, noteTag)}
          content="Reply"
          labelPosition="left"
          icon="paper plane"
          primary
        />
      </Form.Group>
    </Form>
  );
}

export default AddChild;
